import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { pickFields } from 'utils/helpers';
import { addParamsToUrl } from 'utils/query';
import { getUserTableColumnsSignature } from 'utils/user-table-columns';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';
import {
  Project,
  ReportData,
  BaseResponse,
  ProjectAdminConfig,
  PaginationResponse,
  HealthDepartmentOption,
} from 'types';

import { authApi } from '../auth';
import { apiQuery } from '../../query';

import { ProjectFormValues, projectRequestBody } from './request-body';
import {
  DuplicateRequest,
  ProjectListRequest,
  ProjectReportRequest,
  ProjectReportResponse,
} from './types';

const tagType = 'PROJECTS';
const reportTagType = 'PROJECTS_REPORT';
export const healthDepartmentsTagType = 'PROJECT_HEALTH_DEPARTMENTS';
export const healthDepartmentsListTag = {
  id: 'LIST',
  type: healthDepartmentsTagType,
} as const;

const updateFields: (keyof Omit<Project, 'id'>)[] = [
  'userTableColumnsByRole',
  'name',
  'adminTerminology',
  'logo',
  'senderEmail',
  'senderName',
  'auth0Config',
  'isZipRequired',
  'publicName',
  'isPhoneRequired',
  'isBirthdateRequired',
  'publicTenantListEnabled',
  'participantManagementEnabled',
  'allowQuestionCodesAsHeaders',
  'guestLoginRequiresExistingUser',
  'usersMfaOptions',
  'contactUsTemplate',
  'coursePdfTemplate',
  'coursePdfFile',
  'coursePdfPreview',
  'siteConfig',
  'reportsData',
  'publicSignupConfig',
] as const;

export const projectsApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'projectsApi',
  tagTypes: [tagType, reportTagType, healthDepartmentsTagType],
  endpoints: (build) => ({
    delete: build.mutation<void, string>({
      invalidatesTags: invalidatesList(tagType),
      query: (id) => ({
        method: 'delete',
        url: URL.PROJECT_DETAILS.replace(':id', id),
      }),
    }),
    details: build.query<Project, { id: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<Project>) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.PROJECT_DETAILS.replace(':id', id),
      }),
    }),

    create: build.mutation<Project, Omit<ProjectFormValues, 'id'>>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: BaseResponse<Project>) => response.data,
      query: (data) => ({
        method: 'post',
        url: URL.PROJECT_LIST,
        data: projectRequestBody(data),
      }),
    }),

    duplicate: build.mutation<Project, DuplicateRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: BaseResponse<Project>) => response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.PROJECT_DUPLICATE, { id }),
      }),
    }),

    adminConfig: build.query<ProjectAdminConfig, { id: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<ProjectAdminConfig>) =>
        response.data,
      query: ({ id }) => ({
        method: 'get',
        url: generatePath(URL.PROJECT_ADMIN_CONFIG, { id }),
      }),
    }),

    list: build.query<PaginationResponse<Project>, ProjectListRequest | void>({
      providesTags: providesList(tagType),
      transformResponse: (
        response: BaseResponse<PaginationResponse<Project>>
      ) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.PROJECT_LIST, params ?? undefined),
      }),
    }),

    report: build.query<ReportData, ProjectReportRequest>({
      providesTags: [reportTagType],
      transformResponse: (response: ProjectReportResponse) => response.data,
      query: ({ id, reportId, ...rest }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.PROJECT_REPORT, { id, reportId }),
          rest
        ),
      }),
    }),

    healthDepartments: build.query<HealthDepartmentOption[], { id: string }>({
      transformResponse: (response: BaseResponse<HealthDepartmentOption[]>) =>
        response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.PROJECT_HEALTH_DEPARTMENTS.replace(':id', id),
      }),
      providesTags: (_result, _error, { id }) => [
        healthDepartmentsListTag,
        { id, type: healthDepartmentsTagType },
      ],
    }),

    update: build.mutation<Project, ProjectFormValues>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<Project>) => response.data,
      query: ({ id, ...rest }) => ({
        method: 'patch',
        url: URL.PROJECT_DETAILS.replace(':id', id),
        data: projectRequestBody(
          pickFields<Omit<ProjectFormValues, 'id'>>(rest, updateFields)
        ),
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        const project = projectsApi.endpoints.details.select({ id: arg.id })(
          state
        ).data;
        const tenant = authApi.endpoints.tenant.select()(
          state as unknown as Parameters<
            ReturnType<typeof authApi.endpoints.tenant.select>
          >[0]
        ).data;
        const cachedConfig = projectsApi.endpoints.adminConfig.select({
          id: arg.id,
        })(state).data;
        const previousMetadata =
          project ?? (tenant?.projectId === arg.id ? tenant : undefined);
        const previous = previousMetadata ?? cachedConfig;
        const terminologyChanged =
          arg.adminTerminology !== undefined &&
          (!previous ||
            JSON.stringify(arg.adminTerminology) !==
              JSON.stringify(previous.adminTerminology ?? {}));
        const columnsChanged =
          arg.userTableColumnsByRole !== undefined &&
          (!previous ||
            getUserTableColumnsSignature(arg.userTableColumnsByRole) !==
              getUserTableColumnsSignature(previous.userTableColumnsByRole));
        const managementChanged =
          arg.participantManagementEnabled !== undefined &&
          (!previousMetadata ||
            arg.participantManagementEnabled !==
              (previousMetadata.participantManagementEnabled ?? false));
        try {
          await queryFulfilled;
          if (terminologyChanged || managementChanged || columnsChanged) {
            dispatch(authApi.util.invalidateTags(['PROFILE_TENANT']));
          }
        } catch {
          /* failed updates do not invalidate metadata */
        }
      },
    }),
  }),
});

export const {
  useListQuery,
  useReportQuery,
  useDetailsQuery,
  useLazyListQuery,
  useDeleteMutation,
  useCreateMutation,
  useUpdateMutation,
  useLazyDetailsQuery,
  useAdminConfigQuery,
  useDuplicateMutation,
  useHealthDepartmentsQuery,
  useLazyHealthDepartmentsQuery,
} = projectsApi;

export { reportTagType as projectsReportTagType };
